import styled, { css, keyframes } from 'styled-components';

const sizes = [320, 360, 375, 412, 414, 568, 640, 667, 684, 732, 736, 768, 1024, 1366];

const media = sizes.reduce((acc, pxSize) => {
  acc[`max-${pxSize}`] = (...args) => css`
    @media (max-width: ${pxSize}px) {
      ${css(...args)};
    }
  `;
  acc[`min-${pxSize}`] = (...args) => css`
    @media (min-width: ${pxSize}px) {
      ${css(...args)};
    }
  `;
  acc[`max-landscape-${pxSize}`] = (...args) => css`
    @media (max-width: ${pxSize}px) and (orientation: landscape) {
      ${css(...args)};
    }
  `;
  acc[`min-landscape-${pxSize}`] = (...args) => css`
    @media (min-width: ${pxSize}px) and (orientation: landscape) {
      ${css(...args)};
    }
  `;
  return acc;
}, {});

const AlignVertical = styled.div`
  align-items: center;
  display: flex;
  height: 50px;
  ${props => (props.justifyContent ? `justify-content: ${props.justifyContent};` : null)};
`;

const ArrowWrapper = styled(AlignVertical)`
  ${props => (props.bgColor ? `background-color: ${props.bgColor};` : null)} ${props =>
    props.width ? `width: ${props.width};` : null}
  height: auto;
  ${props => (props.margin ? `margin: ${props.margin};` : null)};
  ${props => (props.opacity ? `opacity: ${props.opacity};` : null)};
`;

const ArrowImg = styled.img`
  width: ${props => (props.width ? props.width : '20px')};
  height: auto;
`;

const processing = props => css`
  ${keyframes`
    0% {
      background-color: #2a8c52;
      border: 1px solid #2a8c52;
    }
    50% {
      background-color: #37d678;
      border: 1px solid #37d678;
      box-shadow: 0px 0px 5px 1px rgba(0,0,0,0.2);
    }
    100% {
      background-color: #2a8c52;
      border: 1px solid #2a8c52;
    }
  `}
`;

const Button = styled.input`
  appearance: none;
  background-color: ${({ backgroundColor }) => backgroundColor || '#333333'};
  border: ${border => border || '1px solid #333333'};
  border-radius: ${({ radius }) => radius || '2px'};
  color: white;
  font: inherit;
  font-size: ${({ fontSize }) => fontSize || '15px'};
  font-family: "Avenir Black", "Arial Black", sans-serif;
  letter-spacing: 1px;
  outline: none;
  padding: ${({ padding }) => padding || '3% 3% 2% 3%'};
  text-decoration: none;
  text-transform: uppercase;
  user-select: none;
  transition: all 0.25s ease-in-out;
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '100%'};
  ${props =>
    props.processing
      ? `transition: all .25s ease-in-out;
       animation: ${processing} 1s ease-in-out infinite;`
      : null};
`;

const Anchor = styled.a`
  appearance: none;
  background-color: ${({ backgroundColor }) => backgroundColor || '#333333'};
  border: ${({ border }) => border || '1px solid #333333'};
  border-radius: ${({ radius }) => radius || '2px'};
  color: white;
  font: inherit;
  font-size: ${({ fontSize }) => fontSize || '15px'};
  font-family: "Avenir Black", "Arial Black", sans-serif;
  letter-spacing: 1px;
  outline: none;
  padding: ${({ padding }) => padding || '3% 3% 2% 3%'};
  text-decoration: none;
  text-transform: uppercase;
  user-select: none;
  transition: all 0.25s ease-in-out;
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '100%'};
  box-sizing: border-box;
  display: inline-block;
  line-height: ${({ lineHeight }) => lineHeight || '22px'};

  &:hover {
    color: white;
    text-decoration: none;
  }
`;

const CategoriesButton = styled.div`
  background-color: ${props => (props.open ? '#e5e5e5' : 'white')};
  border: 1px solid #000000;
  border-radius: 5px;
  color: #000000;
  margin: ${props => (props.margin ? props.margin : '0 auto')};
  outline: none;
  padding: 10px;
  text-align: center;
  text-transform: uppercase;
  width: 100%;
  ${props => (props.open ? 'box-shadow: 0px 0px 5px 1px rgba(0,0,0,0.2);' : null)} transition: all .25s ease-in-out;
  height: 37px;
  ${media['min-768']`
    height: 50px;
    padding: 17px;
  `};
`;

const CenterItem = styled.div`
  color: #999;
  margin: ${props => (props.margin ? props.margin : '0 auto')};
  max-width: 500px;
  text-align: ${props => (props.align ? props.align : 'center')};
`;

const ColumnCell = styled.div`
  ${props => (props.debug ? 'background-color: papayawhip;' : null)}
  ${props => (props.debug ? 'border: 1px solid;' : null)}
  ${props => (props.height ? `height: ${props.height}` : null)}
`;

const ColumnGrid = styled.div`
  display: flex;
  flex-direction: column;
  ${props => (props.justifyContent ? `justify-content: ${props.justifyContent};` : null)};
`;

const Heading = styled.div`
  color: #000;
  ${props => (props.fontFamily ? `font-family: ${props.fontFamily};` : null)}
  ${props => (props.fontSize ? `font-size: ${props.fontSize};` : null)}
  ${props => (props.fontWeight ? `font-weight: ${props.fontWeight};` : null)}
  margin: ${props => (props.margin ? props.margin : '20px 10px 5px 15px')};
  text-transform: uppercase;
`;

const Hidden = styled.div`
  display: none;
`;

const StaticPageHeader = styled.div`
  color: #000;
  font-family: "Avenir Black", "Arial Black", sans-serif;
  font-size: ${props => (props.fontSize ? props.fontSize : '15px')};
  margin: ${props => (props.margin ? props.margin : '20px 10px 5px 15px')};
  ${props => (props.fontWeight ? `font-weight: ${props.fontWeight};` : null)};
  ${props => (props.underline ? `text-decoration: underline;` : null)};
`;

const Expand = styled.div`
  align-items: center;
  display: flex;
  height: 50px;
  justify-content: flex-end;
  text-align: right;
  width: 100%;
`;

const Minus = styled.img`
  width: 20px;
  height: auto;
`;

const Plus = Minus;

const ShiftLeftMargin = styled.div`
  ${props => (props.amount ? `margin-left: ${props.amount};` : null)};
`;

const SocialImg = styled.img`
  width: ${props => (props.width ? props.width : 'initial')};
  height: auto;
  ${props => (props.margin ? `margin: ${props.margin};` : null)} user-select: none;
`;

const Wrapper = styled.div`
  ${props => (props.bgColor ? `background-color: ${props.bgColor};` : null)}
  ${props => (props.border ? `border: ${props.border};` : null)}
  ${props => (props.borderBottom ? `border-bottom: ${props.borderBottom};` : null)}
  ${props => (props.borderTop ? `border-top: ${props.borderTop};` : null)}
  ${props => (props.borderRadius ? `border-radius: ${props.borderRadius};` : null)}
  ${props => (props.color ? `color: ${props.color};` : null)}
  ${props => (props.font ? `font-family: ${props.font};` : null)}
  ${props => (props.fontSize ? `font-size: ${props.fontSize};` : null)}
  ${props => (props.fontStyle ? `font-style: ${props.fontStyle};` : null)}
  ${props => (props.fontWeight ? `font-weight: ${props.fontWeight};` : null)}
  ${props => (props.height ? `height: ${props.height};` : null)}
  ${props => (props.letterSpacing ? `letter-spacing: ${props.letterSpacing};` : null)}
  ${props => (props.lineHeight ? `line-height: ${props.lineHeight};` : null)}
  ${props => (props.margin ? `margin: ${props.margin};` : null)}
  ${props => (props.maxWidth ? `max-width: ${props.maxWidth};` : null)}
  ${props => (props.minHeight ? `min-height: ${props.minHeight};` : null)}
  ${props => (props.maxHeight ? `max-height: ${props.maxHeight};` : null)}
  ${props => (props.padding ? `padding: ${props.padding};` : null)}
  ${props => (props.textAlign ? `text-align: ${props.textAlign};` : null)}
  ${props => (props.uppercase ? `text-transform: uppercase;` : null)}
  ${props => (props.width ? `width: ${props.width};` : null)};
`;

const StaticPageWrapper = styled(Wrapper)`
  padding: 15px;
  font-family: "Avenir Light", "Arial", sans-serif;
  line-height: 1.5;
  margin: 0 0 3px 0;
  ${props => (props.fontWeight ? `font-weight: ${props.fontWeight};` : null)} ${props =>
    props.underline ? `text-decoration: underline;` : null};
`;

const StaticPageParagraph = styled.p`
  ${props => (props.fontWeight ? `font-weight: ${props.fontWeight};` : null)};
`;

const SubscribeContentWrapper = styled(Wrapper)`
  margin: 3% 9%;
  ${media['min-568']` margin: 3% 13%; `}
  ${media['min-667']` margin: 3% 19%; `}
  ${media['min-1024']` margin: 3% 29%; `}
  ${media['min-1366']` margin: 3% 34.5%; `}
`;

const SubscribeErrorWrapper = styled.div`
  ${media['min-667']` margin: 0% 13%; `}
  ${media['min-1024']` margin: 0 27%; `}
  ${media['min-1366']` margin: 0 32%; `}
`;

const ModelNumber = styled.div`
  color: #333333;
  font-family: "Avenir Light", "Arial", sans-serif;
  font-weight: bold;
  margin: 5%;
  text-transform: uppercase;
`;

const TriangleDown = styled.div`
  position: absolute;
  top: ${props => (props.top ? props.top : '50px')};
  ${props => (props.left ? `left: ${props.left};` : null)} &:before {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-left: ${props => (props.borderLeft ? props.borderLeft : '10px')} solid transparent;
    border-right: ${props => (props.borderRight ? props.borderRight : '10px')} solid transparent;
    border-top: ${props => (props.active ? '10px solid #333333' : '0px solid #ffffff')};
  }
`;

const triangle = css`
  &:after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    position: absolute;
    margin: 0 auto;
    top: 100%;
    left: 0;
    right: 0;
    border-left: ${props => (props.borderLeft ? props.borderLeft : '10px')} solid transparent;
    border-right: ${props => (props.borderRight ? props.borderRight : '10px')} solid transparent;
    border-top: ${props => (props.active ? '10px solid #333333' : '0px solid #ffffff')};
  }
`;

const LinkWrapper = styled(Wrapper)`
  text-decoration: underline;
  text-transform: uppercase;
  padding: 0 10px 20px 0;
`;

const InlineLink = styled(LinkWrapper)`
  display: inline;
  padding: 0 5px 20px 5px;
`;

const List = styled.ul`
  list-style-type: ${props => (props.listStyleType ? props.listStyleType : 'square')};
  margin: ${props => (props.margin ? props.margin : '2% 0 0 7%')};
`;

const OrderedList = styled.ol`
  margin: 2% 0 0 7%;
  list-style-type: ${props => (props.listStyleType ? props.listStyleType : 'decimal')};
`;

const Image = styled.img`
  ${props => (props.height ? `height: ${props.height};` : null)}
  ${props => (props.width ? `width: ${props.width};` : null)}
  ${props => (props.padding ? `padding: ${props.padding};` : null)}
  ${props => (props.margin ? `margin: ${props.margin};` : null)}
`;

const ReviewitalistBkg = styled.div`
  background-image: url('https://s3-us-west-2.amazonaws.com/italist-img/emails/gradeus_background_mobile.png');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  width: 100%;
  min-height: 550px;
  max-height: 625px;
  padding-top: 70px;
`;

const Primary = styled.div`
  font-family: "Avenir Black", "Arial Black", sans-serif;
  font-size: 30px;
  letter-spacing: 1px;
`;

const Secondary = styled.div`
  font-family: "Avenir Black", "Arial Black", sans-serif;
  font-size: 15px;
  letter-spacing: 1px;
`;

export {
  AlignVertical,
  Anchor,
  ArrowImg,
  ArrowWrapper,
  Button,
  CategoriesButton,
  CenterItem,
  ColumnCell,
  ColumnGrid,
  Expand,
  Heading,
  Hidden,
  Image,
  InlineLink,
  LinkWrapper,
  List,
  media,
  Minus,
  ModelNumber,
  OrderedList,
  Plus,
  Primary,
  ReviewitalistBkg,
  Secondary,
  ShiftLeftMargin,
  SocialImg,
  StaticPageHeader,
  StaticPageWrapper,
  StaticPageParagraph,
  SubscribeContentWrapper,
  SubscribeErrorWrapper,
  triangle,
  TriangleDown,
  Wrapper
};
